import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Share, PlusSquare, MoreVertical, Download, Smartphone, CheckCircle2, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from '../components/ui/sonner';

function detectPlatform() {
  const ua = navigator.userAgent || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
  const isAndroid = /android/i.test(ua);
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
  return { isIOS, isAndroid, isStandalone };
}

export default function MobileDownload() {
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(detectPlatform);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const url = `${window.location.origin}/mobile`;

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setPlatform(detectPlatform());
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('链接已复制');
    } catch (e) {
      toast.error('复制失败，请手动复制：' + url);
    }
  };

  const isMobileDevice = platform.isIOS || platform.isAndroid;

  return (
    <div data-testid="mobile-download-container" className="flex min-h-screen w-full flex-col items-center bg-[#F8FAFC] px-6 py-10">
      <button
        onClick={() => navigate('/')}
        data-testid="mobile-back-to-home-btn"
        className="mb-8 flex w-full max-w-md items-center gap-2 rounded-full py-2 font-body text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> 返回首页
      </button>

      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#3B82F6]/10 text-[#3B82F6]">
          <Smartphone className="h-8 w-8" />
        </div>
        <h1 className="mb-2 font-heading text-2xl font-semibold text-slate-900">获取手机版</h1>
        <p className="mb-6 font-body text-sm text-slate-500">
          无需应用商店，添加到主屏幕即可像 App 一样打开使用
        </p>

        {platform.isStandalone ? (
          <div data-testid="mobile-already-installed" className="space-y-4">
            <div className="flex items-center justify-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 font-body text-sm text-emerald-600">
              <CheckCircle2 className="h-4 w-4" /> 已经在应用模式中打开
            </div>
            <Button
              data-testid="mobile-enter-app-btn"
              onClick={() => navigate('/')}
              className="h-12 w-full rounded-full bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90"
            >
              进入会议系统
            </Button>
          </div>
        ) : !isMobileDevice ? (
          <div data-testid="mobile-qr-panel" className="space-y-5">
            <div className="flex items-center justify-center rounded-xl border border-slate-100 bg-slate-50 p-6">
              <QRCodeSVG data-testid="mobile-qr-code" value={url} size={200} fgColor="#0f172a" bgColor="#ffffff" level="M" />
            </div>
            <p className="font-body text-xs text-slate-400">使用手机相机或浏览器扫码打开本页面</p>
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
              <span data-testid="mobile-share-url" className="flex-1 truncate text-left font-mono text-xs text-slate-500">
                {url}
              </span>
              <button data-testid="mobile-copy-link-btn" onClick={handleCopyLink} className="shrink-0 text-slate-500 hover:text-slate-900">
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
        ) : deferredPrompt ? (
          <div data-testid="mobile-android-install" className="space-y-4">
            <Button
              data-testid="mobile-install-btn"
              onClick={handleInstall}
              className="h-12 w-full rounded-full bg-[#3B82F6] text-white hover:bg-[#3B82F6]/90"
            >
              <Download className="mr-2 h-4 w-4" /> 安装到手机
            </Button>
          </div>
        ) : platform.isIOS ? (
          <div data-testid="mobile-ios-instructions" className="space-y-3 text-left">
            <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3B82F6] font-mono text-xs text-white">1</span>
              <p className="font-body text-sm text-slate-600">
                点击浏览器底部的分享图标 <Share className="inline h-4 w-4 -translate-y-0.5" />
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3B82F6] font-mono text-xs text-white">2</span>
              <p className="font-body text-sm text-slate-600">
                选择"添加到主屏幕" <PlusSquare className="inline h-4 w-4 -translate-y-0.5" />
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3B82F6] font-mono text-xs text-white">3</span>
              <p className="font-body text-sm text-slate-600">点击"添加"完成安装</p>
            </div>
          </div>
        ) : (
          <div data-testid="mobile-android-manual-instructions" className="space-y-3 text-left">
            <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3B82F6] font-mono text-xs text-white">1</span>
              <p className="font-body text-sm text-slate-600">
                点击浏览器右上角菜单 <MoreVertical className="inline h-4 w-4 -translate-y-0.5" />
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-slate-50 p-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#3B82F6] font-mono text-xs text-white">2</span>
              <p className="font-body text-sm text-slate-600">选择"安装应用"或"添加到主屏幕"</p>
            </div>
          </div>
        )}

        {!platform.isStandalone && (
          <button
            data-testid="mobile-skip-enter-btn"
            onClick={() => navigate('/')}
            className="mt-6 font-body text-sm text-slate-400 underline-offset-2 hover:text-slate-700 hover:underline"
          >
            暂不安装，直接进入会议系统
          </button>
        )}
      </div>
    </div>
  );
}
